import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, getToken } from "../../../software/api";
import { API, todayISO } from "./shared";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Upload, Plus, Play, SlidersHorizontal, FlaskConical, Camera, ChartSpline, CheckCircle2, Loader2, Trash2 } from "lucide-react";
import {
  TEST_TYPES, SPIN_DISKS, WB_TYPES, SURFACES, FILTERS, WATER_SOURCES, MAG_LEVELS, CHEM_BRANDS,
  CATEGORIES, catLabel, computeLSI, lsiState, lsiPercent, resolveParams, resolveAll, evalValue, autoDisk, flattenRange, rangeCatFor, canonReadings,
} from "./waterChem";
import { printAnalysis, printCertificate, buildEmailHtml, buildSmsText, recommendations, printGreenGuide } from "./waterReport";
import { loadLayouts, promoKey, storeCompany, STORE_COMPANY, fetchLiveCards, withLive } from "./waterLayout";
import { useAuth } from "../../../software/AuthContext";
import { computeMineralDose, isMineralBody } from "./mineralSalt";
import { StripReader } from "./StripReader";
import { SpinTesterButton } from "./SpinTester";
import { ReadingHistory } from "./ReadingHistory";
import { DosingPopup } from "./DosingPopup";
import { stepFromRec, mergeSteps, priorityOf } from "./dosing";

const CONFIG_KEYS = ["name", "category", "type", "surface", "volume_litres", "filter_type", "water_source", "salt_level", "magnesium_level", "chemical_brand", "equipment_image_url", "pool_image_url", "covered", "notes", "range_overrides"];
const emptyBody = () => ({ name: "", category: "pool", type: "", surface: "", volume_litres: 0, filter_type: "", water_source: "", salt_level: "", magnesium_level: "", chemical_brand: "", equipment_image_url: "", pool_image_url: "", covered: false, notes: "", range_overrides: {} });

const wselect = "w-full bg-white/90 border border-[#7ba7d9] rounded-sm px-2 py-0.5 text-[13px] text-[#123a63]";
const winput = "bg-[#cfeaf5] border border-[#7ba7d9] rounded-sm px-2 py-0.5 text-[13px] font-bold text-center w-20 focus:bg-white outline-none";

function Sel({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-2 mb-0.5">
      <label className="text-[13px] font-black text-[#123a63] w-44 shrink-0">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={wselect} data-testid={testid}>
        <option value=""></option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export function WaterTestTab({ cid, bodies, tests, brands, client, reload, onSellAtPos, onGoToClientDetails }) {
  const [company, setCompany] = useState(STORE_COMPANY);
  useEffect(() => { api("GET", "/company-setup").then((cs) => setCompany(storeCompany(cs))).catch(() => {}); }, []);
  const [activeBodyId, setActiveBodyId] = useState(bodies[0]?.id || null);
  const [adding, setAdding] = useState(false);
  const [testType, setTestType] = useState("Spin Disk");
  const [disk, setDisk] = useState("304");
  const [testDate, setTestDate] = useState(todayISO());
  const [readings, setReadings] = useState({});
  const [historyId, setHistoryId] = useState("");
  const [cfg, setCfg] = useState(emptyBody());
  const [savingCfg, setSavingCfg] = useState(false);
  const [templatesByType, setTemplatesByType] = useState({});
  const [ranges, setRanges] = useState([]);
  const [dosing, setDosing] = useState(null);
  const [rangeEdit, setRangeEdit] = useState(false);
  const [showStrip, setShowStrip] = useState(false);
  const [histParam, setHistParam] = useState(null);
  const [steps, setSteps] = useState([]);
  const [green, setGreen] = useState(false);
  const [treat, setTreat] = useState({});
  const [layouts, setLayouts] = useState(() => loadLayouts(null));
  const { user } = useAuth();
  useEffect(() => { api("GET", "/water-test-layout").then((v) => setLayouts(loadLayouts(v))).catch(() => {}); }, []);
  const [showDosing, setShowDosing] = useState(false);
  const [saveState, setSaveState] = useState("");
  const [saveTick, setSaveTick] = useState(0);
  const dirty = useRef(false);
  const saving = useRef(false);
  const draftId = useRef("");
  useEffect(() => {
    const tc = parseFloat(readings.total_chlorine), fc = parseFloat(readings.free_chlorine);
    const cc = Number.isFinite(tc) && Number.isFinite(fc) ? String(Math.max(0, Math.round((tc - fc) * 100) / 100)) : undefined;
    if (cc !== undefined && readings.combined_chlorine !== cc) setReadings((r) => ({ ...r, combined_chlorine: cc }));
  }, [readings.total_chlorine, readings.free_chlorine, readings.combined_chlorine]);
  const base = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { api("GET", "/water-templates").then((list) => { const m = {}; (list || []).forEach((t) => { m[t.type] = t; }); setTemplatesByType(m); }).catch(() => {}); }, []);
  useEffect(() => { api("GET", "/chemical-ranges").then(setRanges).catch(() => setRanges([])); }, []);
  useEffect(() => { api("GET", "/dosing-rules").then(setDosing).catch(() => setDosing(null)); }, []);
  const rangesByName = useMemo(() => Object.fromEntries((ranges || []).map((r) => [r.name, r])), [ranges]);
  const rangeNames = useMemo(() => (ranges || []).filter((r) => r.active).map((r) => r.name), [ranges]);
  useEffect(() => { if (!activeBodyId && bodies[0]) setActiveBodyId(bodies[0].id); }, [bodies, activeBodyId]);
  const activeBody = useMemo(() => bodies.find((b) => b.id === activeBodyId) || null, [bodies, activeBodyId]);

  const resolved = useMemo(() => resolveParams({ type: cfg.type, category: cfg.category, range_overrides: cfg.range_overrides }, templatesByType), [cfg.type, cfg.category, cfg.range_overrides, templatesByType]);

  useEffect(() => {
    if (!activeBody) { setCfg(emptyBody()); return; }
    const c = emptyBody();
    CONFIG_KEYS.forEach((k) => { if (activeBody[k] != null) c[k] = activeBody[k]; });
    setCfg(c);
    setReadings({}); setSteps([]); setGreen(false); setTreat({}); setHistoryId(""); dirty.current = false;
  }, [activeBodyId]); // eslint-disable-line

  const bodyTests = useMemo(() => tests.filter((t) => t.water_body_id === activeBodyId), [tests, activeBodyId]);

  // Auto-select the LaMotte spin disk from the body type + leafy flag (204/304/801).
  useEffect(() => {
    if (activeBody) setDisk(autoDisk(activeBody));
  }, [activeBodyId, activeBody?.type, activeBody?.category, activeBody?.leafy]); // eslint-disable-line react-hooks/exhaustive-deps

  const lsi = computeLSI(readings);
  const lState = lsiState(lsi);

  const addBody = async (category) => {
    try {
      const b = await api("POST", `/clients/${cid}/waterbodies`, { name: catLabel(category), category });
      await reload(); setActiveBodyId(b.id); setAdding(false);
      toast.success(`${catLabel(category)} added`);
    } catch (e) { toast.error(e.message); }
  };

  const saveConfig = async (patch = {}) => {
    if (!activeBody) return null;
    const next = { ...cfg, ...patch };
    setCfg(next); setSavingCfg(true);
    try {
      const payload = {}; CONFIG_KEYS.forEach((k) => { payload[k] = next[k]; });
      payload.volume_litres = parseInt(next.volume_litres) || 0;
      const r = await api("PATCH", `/waterbodies/${activeBody.id}`, payload);
      await reload();
      return r;
    } catch (e) { toast.error(e.message); return null; } finally { setSavingCfg(false); }
  };

  const uploadImg = async (field, file) => {
    if (!activeBody) { toast.error("Add a body of water first"); return; }
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/uploads`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Upload failed"); }
      const d = await res.json();
      await saveConfig({ [field]: d.url });
      toast.success("Image uploaded");
    } catch (e) { toast.error(e.message); }
  };

  const [spinInbox, setSpinInbox] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = () => api("GET", "/water-tests/inbox").then((r) => { if (alive) setSpinInbox(r || []); }).catch(() => {});
    load();
    const t = setInterval(load, 12000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const applySpin = (item) => {
    const r = {}; Object.entries(item.readings || {}).forEach(([k, v]) => { if (k !== "salt" && k !== "tds") r[k] = String(v); });
    applyReadings(r);
    api("POST", `/water-tests/inbox/${item.id}/discard`).catch(() => {});
    setSpinInbox((list) => list.filter((x) => x.id !== item.id));
    toast.success("Spin Touch reading loaded and saved");
  };
  const discardSpin = (item) => {
    api("POST", `/water-tests/inbox/${item.id}/discard`).catch(() => {});
    setSpinInbox((list) => list.filter((x) => x.id !== item.id));
  };

  const loadHistory = (id) => {
    dirty.current = false; setSaveState("");
    setHistoryId(id);
    const t = bodyTests.find((x) => x.id === id);
    if (!t) { setReadings({}); setSteps([]); setGreen(false); setTreat({}); return; }
    setTreat(t.treatments || {});
    setSteps(Array.isArray(t.dosing_steps) ? t.dosing_steps : []);
    setGreen(!!t.green_pool);
    const r = {}; Object.entries(canonReadings(t.readings)).forEach(([k, v]) => { r[k] = String(v); });
    setReadings(r);
    if (t.test_type) setTestType(t.test_type);
    if (t.disk) setDisk(t.disk);
    if (t.test_date) setTestDate(t.test_date.slice(0, 10));
  };

  const currentTest = () => ({
    readings: Object.fromEntries(Object.entries(readings).map(([k, v]) => [k, parseFloat(v)]).filter(([, v]) => Number.isFinite(v))),
    lsi, test_type: testType, disk: testType === "Spin Disk" ? disk : "", test_date: testDate,
    operator: user?.name || "", notes: "", brand: cfg.chemical_brand, green_pool: green, treatments: treat,
  });

  const saveNow = async () => {
    if (!dirty.current || !activeBody) return;
    {
      const cur = currentTest();
      if (saving.current || !Object.keys(cur.readings).length) return;
      dirty.current = false; saving.current = true; setSaveState("saving");
      const payload = { source: "manual", readings: cur.readings, lsi: cur.lsi, test_type: cur.test_type, disk: cur.disk,
        test_date: cur.test_date, water_body_id: activeBody.id, water_body_name: catLabel(activeBody.category), dosing_steps: steps, green_pool: green, treatments: treat };
      try {
        if (historyId) await api("PUT", `/tests/${historyId}`, payload);
        else { const d = await api("POST", `/clients/${cid}/tests`, payload); draftId.current = d.id; setHistoryId(d.id); }
        setSaveState("saved"); reload();
      } catch (e) { dirty.current = true; setSaveState("error"); toast.error(`Auto-save failed: ${e.message}`); }
      finally { saving.current = false; if (dirty.current) setSaveTick((x) => x + 1); }
    }
  };
  useEffect(() => {
    if (!dirty.current || !activeBody) return;
    const t = setTimeout(saveNow, 1200);
    return () => clearTimeout(t);
  }, [readings, testDate, saveTick, steps, green, treat]); // eslint-disable-line react-hooks/exhaustive-deps
  const deleteTest = async () => {
    const t = bodyTests.find((x) => x.id === historyId);
    const label = t ? new Date(t.test_date || t.created_at).toLocaleDateString("en-AU") : "this";
    if (!window.confirm(`Delete the ${label} water test? This cannot be undone.`)) return;
    try {
      dirty.current = false;
      await api("DELETE", `/tests/${historyId}`);
      if (draftId.current === historyId) draftId.current = null;
      setReadings({}); setSteps([]); setGreen(false); setTreat({}); setHistoryId("");
      toast.success("Water test deleted"); reload();
    } catch (e) { toast.error(e.message); }
  };
  const applyReadings = (r) => { setReadings((p) => ({ ...p, ...r })); setHistoryId((h) => (h && h === draftId.current ? h : "")); dirty.current = true; };
  const enterNext = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const all = [...document.querySelectorAll("[data-wt-reading]:not([readonly])")];
    const next = all[all.indexOf(e.currentTarget) + 1];
    if (next) { next.focus(); next.select(); } else e.currentTarget.blur();
  };

  const brand = cfg.chemical_brand || (brands && brands[0]) || "Aqualux Nexus";
  // bodies without a brand use the store's BioGuard range (else the first range)
  const activeRange = rangesByName[cfg.chemical_brand] || rangesByName.BioGuard || (ranges || [])[0] || null;
  const productMap = useMemo(() => flattenRange(activeRange, activeBody?.category || cfg.category) || null, [activeRange, activeBody?.category, cfg.category]);
  const [catalogue, setCatalogue] = useState([]);
  useEffect(() => { api("GET", "/products").then((l) => setCatalogue(l || [])).catch(() => setCatalogue([])); }, []);
  const catByName = useMemo(() => { const m = {}; (catalogue || []).forEach((p) => { if (p.name) m[p.name.toLowerCase()] = p; }); return m; }, [catalogue]);
  const rawLayout = layouts[promoKey(cfg)];
  const [liveCards, setLiveCards] = useState([]);
  const cardKey = JSON.stringify((rawLayout.page2.cards || []).map((c) => [c.product_id, c.title]));
  useEffect(() => { fetchLiveCards(api, rawLayout.page2.cards).then(setLiveCards); }, [cardKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const layout = useMemo(() => withLive(rawLayout, liveCards), [rawLayout, liveCards]);
  const matrixForCat = useMemo(() => {
    return activeRange && activeRange.matrix ? (activeRange.matrix[rangeCatFor(activeBody?.category || cfg.category)] || null) : null;
  }, [activeRange, activeBody, cfg.category]);
  const oxidisers = useMemo(() => (matrixForCat?.oxidiser || []).map((p) => p.name).filter(Boolean), [matrixForCat]);
  const recs = useMemo(() => { try { return recommendations(readings, cfg, resolved, brand, productMap, layout.rec_texts, treat, oxidisers); } catch { return []; } }, [readings, cfg, resolved, brand, productMap, layout.rec_texts, treat, oxidisers]);
  const mineralDose = useMemo(() => (isMineralBody(activeBody) ? computeMineralDose({ readings, body: activeBody, rules: dosing }) : { applicable: false }), [readings, activeBody, dosing]);
  useEffect(() => { setSteps((prev) => mergeSteps(prev, recs, (r) => stepFromRec(r, matrixForCat, catByName))); }, [recs, matrixForCat, catByName]);
  const editSteps = (next) => { setSteps(next); dirty.current = true; };
  // other products staff can swap a step to: same-role products in the brand range (sanitiser/oxidiser share a list)
  const alternativesFor = (s) => {
    const chlor = s.action === "sanitiser" || s.action === "oxidiser";
    const roles = chlor ? ["oxidiser", "sanitiser"] : [s.action];
    const names = [...(chlor ? ["Salt Pool Sparkle", "Lite", "Burnout Extreme"] : []), ...roles.flatMap((r) => (matrixForCat?.[r] || []).map((p) => p.name))];
    const salt = /salt|mineral|magna/i.test(`${cfg.type || ""} ${cfg.sanitiser || ""}`) || !!cfg.salt_level;
    return [...new Set([s.sku_product, ...names].filter(Boolean))].filter((n) => !(salt && /stick|tab/i.test(n)) || n === s.sku_product);
  };
  const swapProduct = (id, name, partPrice) => editSteps(steps.map((s) => {
    if (s.id !== id) return s;
    const old = s.sku_product || "";
    const title = old && s.title.includes(old) ? s.title.replace(old, name) : s.title;
    const b = stepFromRec({ action: s.action, key: s.key, title, text: s.text, product: name, amount: s.amount, unit: s.unit }, matrixForCat, catByName);
    return { ...b, price: b.price ?? (partPrice != null ? Number(partPrice) : null), id: s.id, include: s.include, base: s.base, custom: s.custom };
  }));
  const mineralStep = steps.find((s) => s.id === "mineral");
  const chooseMineral = (opt) => {
    // the mineral option already covers the salt, so the plain salt step is switched off while one is chosen
    const rest = steps.filter((s) => s.id !== "mineral").map((s) => (s.action === "salt" && !s.custom ? { ...s, include: !!(mineralStep && mineralStep.mineral_label === opt.label) } : s));
    if (mineralStep && mineralStep.mineral_label === opt.label) { editSteps(rest); return; }
    const desc = opt.lines.map((l) => `${l.qty} × ${l.name}`).join(" + ");
    const st = { id: "mineral", custom: true, key: "mineral", action: "salt", include: true, mineral_label: opt.label, title: `Add ${desc}`, product: opt.label,
      lines: opt.lines.map((l) => ({ name: l.name, qty: l.qty, part_id: l.part_id })), price: opt.total, text: layout.rec_texts.mineral || "" };
    const at = rest.findIndex((s) => priorityOf(s) > 6);
    editSteps(at < 0 ? [...rest, st] : [...rest.slice(0, at), st, ...rest.slice(at)]);
  };
  const included = steps.filter((x) => x.include && (x.title || x.product));
  const perfect = Object.keys(readings).length > 0 && !included.length && !mineralDose.applicable
    && resolved.every((p) => { const v = parseFloat(readings[p.key]); return !Number.isFinite(v) || (v >= p.min && v <= p.max); });
  const printCert = () => printCertificate({ test: currentTest(), body: cfg, client, company, params: resolved });
  const doReport = (autoprint) => {
    const t = { ...currentTest(), created_at: new Date().toISOString() };
    if (!Object.keys(t.readings).length) { toast.error("Enter readings first"); return; }
    printAnalysis({ test: t, body: cfg, client, brand, company, params: resolved, productMap, steps, mineralOptions: mineralStep ? null : mineralDose, autoprint, layout, green });
  };
  const clientEmail = () => (client?.email || (client?.contacts || []).map((c) => c.email).find(Boolean) || "").trim();
  const doEmail = async () => {
    const t = { ...currentTest(), created_at: new Date().toISOString() };
    if (!Object.keys(t.readings).length) { toast.error("Enter readings first"); return; }
    const to = clientEmail();
    if (!to) { toast.error("No email on file for this client — add one on the Client Details tab."); return; }
    const html = buildEmailHtml({ test: t, body: cfg, client, brand, company, params: resolved, productMap, steps });
    try {
      await api("POST", "/email/send", { recipient_email: to, subject: `Your water analysis — ${cfg?.name || catLabel(cfg?.category)}`, html_content: html });
      toast.success(`Water analysis emailed to ${to}`);
    } catch (e) { toast.error(e.message); }
  };
  const doSms = () => {
    const t = { ...currentTest(), created_at: new Date().toISOString() };
    if (!Object.keys(t.readings).length) { toast.error("Enter readings first"); return; }
    buildSmsText({ test: t, body: cfg, client, brand, company, params: resolved, productMap, steps }); // content ready for Twilio wiring
    toast.info("SMS sending activates once your Twilio key is added — results embed inline in the message.");
  };
  const sellAtPos = async () => {
    const t = currentTest();
    if (!Object.keys(t.readings).length) { toast.error("Enter readings first"); return; }
    const items = included.flatMap((x) => x.lines || [{ name: x.custom ? x.product : x.sku_product || x.product, qty: 1, price: x.price }]).filter((x) => x.name);
    if (!items.length) { toast.info("Nothing ticked to sell."); return; }
    await saveNow();
    if (onSellAtPos) onSellAtPos(client, items, activeRange?.name || brand);
    else toast.error("POS is not available from here.");
  };
  const runAqualux = () => toast.info("Aqualux Nexus tester link is pending — the on-board button on the tester will trigger this test. Enter results in the Value column for now.");

  return (
    <div data-testid="co-water-tests">
      {/* body sub-tabs */}
      <div className="flex items-end gap-1 border-b-2 border-[#5b8ac4] mb-1.5">
        {bodies.map((b) => (
          <button key={b.id} onClick={() => setActiveBodyId(b.id)} data-testid={`wt-body-${b.id}`}
            className={`px-5 py-1 text-[13px] font-black rounded-t-md border border-b-0 ${b.id === activeBodyId ? "bg-[#dbe9f7] text-[#123a63] border-[#5b8ac4]" : "bg-[#eef3f9] text-slate-500 border-slate-300 hover:bg-white"}`}>
            {catLabel(b.category)}
          </button>
        ))}
        <div className="relative">
          <button onClick={() => setAdding((v) => !v)} data-testid="wt-add-body-btn"
            className="px-4 py-1 text-[13px] font-black text-emerald-700 rounded-t-md border border-b-0 border-emerald-300 bg-[#eef7ef] hover:bg-white flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> ADD +
          </button>
          {adding && (
            <div className="absolute z-20 mt-1 bg-white border border-[#7ba7d9] rounded shadow-lg p-1" data-testid="wt-add-menu">
              {CATEGORIES.map(([c, l]) => (
                <button key={c} onClick={() => addBody(c)} data-testid={`wt-add-${c}`}
                  className="block w-full text-left px-4 py-1.5 text-[13px] hover:bg-[#dbe9f7] text-[#123a63] rounded-sm">{l}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {!activeBody ? (
        <p className="p-4 text-slate-500 text-sm">No bodies of water yet — click <b>ADD +</b> to add a Pool, Spa or Swim Spa.</p>
      ) : (
        <div className="rounded-md p-2" style={{ backgroundImage: "linear-gradient(160deg,#cfe8f5,#a9d3ec)" }}>
          {/* top control bar — test type + compact action buttons + history all on one line */}
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <div className="flex items-center gap-2">
              <label className="text-[13px] font-black text-[#123a63]">Test Type</label>
              <select value={testType} onChange={(e) => setTestType(e.target.value)} className={wselect + " w-36"} data-testid="wt-test-type">
                {TEST_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              {testType === "Spin Disk" && (
                <select value={disk} onChange={(e) => setDisk(e.target.value)} className={wselect + " w-24"} data-testid="wt-disk">
                  {SPIN_DISKS.map((d) => <option key={d} value={d}>Disk {d}</option>)}
                </select>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => doReport(false)} data-testid="wt-view-analysis" className="px-3 py-1 rounded-sm bg-[#7fb0dd] border border-[#4a7fb5] text-[#0d2c4d] font-black text-[12px] hover:bg-[#9cc4e6]">VIEW</button>
              <button onClick={() => doReport(true)} data-testid="wt-print-analysis" className="px-3 py-1 rounded-sm bg-[#7fb0dd] border border-[#4a7fb5] text-[#0d2c4d] font-black text-[12px] hover:bg-[#9cc4e6]">PRINT</button>
              <label className="flex items-center gap-1 px-2 py-1 rounded-sm bg-[#d8f5d0] border border-[#5aa24a] text-[#1d5a12] font-black text-[12px] cursor-pointer select-none" title="Pool is Green — prints the Green Pool guide with the report">
                <input type="checkbox" checked={green} onChange={(e) => { setGreen(e.target.checked); dirty.current = true; }} data-testid="wt-green-pool" className="accent-[#2e8b2e]" />Pool is Green
              </label>
              {[["metal_stain", "Metal Stains"], ["general_stain", "General Stains"]].map(([k, t]) => (
                <label key={k} className="flex items-center gap-1 px-2 py-1 rounded-sm bg-[#fdf1d8] border border-[#d79b36] text-[#6b4306] font-black text-[12px] cursor-pointer select-none" title={`Client reports: ${t}`}>
                  <input type="checkbox" checked={!!treat[k]} onChange={(e) => { setTreat((x) => ({ ...x, [k]: e.target.checked })); dirty.current = true; }} data-testid={`wt-treat-${k}`} className="accent-[#b5610f]" />{t}
                </label>
              ))}
              <select value={typeof treat.floc === "string" ? treat.floc : treat.floc ? "Power Floc" : ""} onChange={(e) => { setTreat((x) => ({ ...x, floc: e.target.value || false })); dirty.current = true; }}
                data-testid="wt-treat-floc" title="Mud / dead algae that will not settle"
                className={`px-1.5 py-1 rounded-sm border font-black text-[12px] cursor-pointer ${treat.floc ? "bg-[#fdf1d8] border-[#d79b36] text-[#6b4306]" : "bg-white border-[#d79b36] text-[#6b4306]"}`}>
                <option value="">Needs Floc…</option><option value="Power Floc">Floc: Power Floc</option><option value="One Shot Floc">Floc: One Shot Floc</option>
              </select>
              <button onClick={() => printGreenGuide({ layout, company, client })} data-testid="wt-print-green-guide" className="px-3 py-1 rounded-sm bg-[#7fb0dd] border border-[#4a7fb5] text-[#0d2c4d] font-black text-[12px] hover:bg-[#9cc4e6]">GREEN POOL GUIDE</button>
              <button onClick={doEmail} data-testid="wt-email-analysis" className="px-3 py-1 rounded-sm bg-[#7fb0dd] border border-[#4a7fb5] text-[#0d2c4d] font-black text-[12px] hover:bg-[#9cc4e6]">EMAIL</button>
              <button onClick={doSms} data-testid="wt-sms-analysis" className="px-3 py-1 rounded-sm bg-[#7fb0dd] border border-[#4a7fb5] text-[#0d2c4d] font-black text-[12px] hover:bg-[#9cc4e6]">SMS</button>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {(() => {
                const t = bodyTests.find((x) => x.test_date || x.created_at);
                if (!t) return null;
                const today = new Date().toLocaleDateString("en-CA");
                const last = t.test_date ? t.test_date.slice(0, 10) : t.created_at ? new Date(t.created_at).toLocaleDateString("en-CA") : "";
                const next = t.next_test_date || "";
                const overdue = next && next < today;
                const f = (d) => (d ? new Date(d).toLocaleDateString("en-AU") : "");
                return (
                  <span data-testid="wt-glance" className={`text-[11px] font-black px-2 py-0.5 rounded-sm border ${overdue ? "bg-rose-100 text-rose-800 border-rose-400" : "bg-emerald-100 text-emerald-800 border-emerald-500"}`}>
                    Last {f(last)}{next ? ` · Due ${f(next)}` : ""}{overdue ? " · OVERDUE" : ""}
                  </span>
                );
              })()}
              <label className="text-[13px] font-black text-[#123a63]">HISTORY</label>
              <select value={historyId} onChange={(e) => loadHistory(e.target.value)} className={wselect + " w-44"} data-testid="wt-history">
                <option value="">— New test —</option>
                {bodyTests.map((t) => <option key={t.id} value={t.id}>{new Date(t.test_date || t.created_at).toLocaleDateString("en-AU")} · {t.source === "bioguard_history" ? "Water Test" : t.test_type || t.source}</option>)}
              </select>
              {historyId && (
                <button onClick={deleteTest} data-testid="wt-history-delete" title="Delete this water test"
                  className="px-2 py-1 rounded-sm bg-white border border-[#b91c1c] text-[#b91c1c] font-black text-[12px] hover:bg-[#fde8e8] flex items-center gap-1 whitespace-nowrap">
                  <Trash2 className="h-3.5 w-3.5" />DELETE TEST
                </button>
              )}
              {(() => {
                const sel = bodyTests.find((x) => x.id === historyId);
                const pdf = sel?.pdf_url_archived ? `${base}${sel.pdf_url_archived}` : sel?.pdf_url;
                return pdf ? (
                  <a href={pdf} target="_blank" rel="noopener noreferrer" data-testid="wt-history-pdf"
                     className="px-3 py-1 rounded-sm bg-[#b91c1c] border border-[#7f1010] text-white font-black text-[12px] hover:bg-[#dc2626] whitespace-nowrap">
                    VIEW PDF REPORT
                  </a>
                ) : null;
              })()}
            </div>
          </div>

          {/* device action per test type */}
          <div className="mb-1.5" data-testid="wt-device-action">
            {testType === "Spin Disk" && (
              <SpinTesterButton disk={disk} body={activeBody} clientId={cid} onReadings={applyReadings} />
            )}
            {testType === "Aqualux Nexus Pool Shop" && (
              <button onClick={runAqualux} data-testid="wt-run-aqualux" className="w-full py-1 rounded-sm bg-emerald-700 text-white font-black text-[13px] hover:bg-emerald-800 flex items-center justify-center gap-2">
                <Play className="h-4 w-4" fill="#fff" /> Start Aqualux Water Test
              </button>
            )}
            {testType === "Manual" && (
              <div className="flex items-center gap-2">
                <button onClick={() => setShowStrip(true)} data-testid="wt-read-strip" className="shrink-0 px-4 py-1 rounded-sm bg-[#0e7490] hover:bg-[#0b5e75] text-white font-black text-[13px] flex items-center gap-1.5"><Camera className="h-4 w-4" /> Read Test Strip</button>
                <div className="flex-1 text-center text-[12px] font-bold text-[#123a63] bg-white/60 border border-[#7ba7d9] rounded-sm py-1" data-testid="wt-manual-hint">
                  Manual entry — photograph a test strip (any saved brand), or type each result into the Value column below.
                </div>
              </div>
            )}
            {showStrip && <StripReader onClose={() => setShowStrip(false)} onApply={(r) => { applyReadings(r); setShowStrip(false); }} />}
            {histParam && <ReadingHistory param={histParam} tests={bodyTests} onClose={() => setHistParam(null)} />}
          </div>

          {spinInbox.length > 0 && (
            <div className="mb-2 rounded-md border-2 border-[#e08a1e] bg-[#fff7ec] p-2" data-testid="wt-spin-inbox">
              <div className="flex items-center gap-2 mb-1 text-[13px] font-black text-[#b5610f]">
                <FlaskConical className="h-4 w-4" /> Spin Touch reading{spinInbox.length > 1 ? "s" : ""} ready to file ({spinInbox.length}) — open the right customer, then Apply
              </div>
              <div className="space-y-1">
                {spinInbox.slice(0, 3).map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-2 bg-white/70 border border-[#f0c48a] rounded-sm px-2 py-1">
                    <div className="text-[12px] text-[#123a63] min-w-0">
                      <span className="font-bold">{item.terminal}</span>
                      <span className="text-slate-500"> · {new Date(item.created_at).toLocaleTimeString()}</span>
                      <span className="ml-2 text-slate-600">{Object.keys(item.readings || {}).length} values</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => applySpin(item)} data-testid="wt-spin-apply" className="px-3 py-1 rounded-sm bg-[#e08a1e] text-white font-black text-[12px] hover:bg-[#c9791a]">Apply to this test</button>
                      <button onClick={() => discardSpin(item)} data-testid="wt-spin-discard" className="px-2 py-1 rounded-sm border border-slate-300 text-slate-600 text-[12px] hover:bg-slate-50">Discard</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-2">
            {/* readings grid */}
            <div className="flex-1 min-w-0 bg-white/85 border border-[#7ba7d9] rounded-md p-2">
              <div className="flex justify-between items-center gap-2 mb-1">
                <button onClick={() => setRangeEdit(true)} data-testid="wt-adjust-ranges" className="flex items-center gap-1 text-[12px] font-bold text-[#123a63] bg-white/70 border border-[#7ba7d9] rounded-sm px-2 py-0.5 hover:bg-white"><SlidersHorizontal className="h-3.5 w-3.5" /> Adjust ranges for this {catLabel(cfg.category).toLowerCase()}</button>
                <div className="flex items-center gap-2">
                  <label className="text-[13px] font-bold text-[#123a63]">Date</label>
                  <input type="date" value={testDate} onChange={(e) => { dirty.current = true; setTestDate(e.target.value); }} className="bg-[#cfeaf5] border border-[#7ba7d9] rounded-sm px-2 py-0.5 text-[13px]" data-testid="wt-date" />
                </div>
              </div>
              <div className="grid grid-cols-[1.4fr_0.7fr_0.9fr_0.7fr_1.6fr] gap-x-2 text-[12px] font-black text-[#123a63] px-1 mb-0.5">
                <span></span><span className="text-center">Minimum</span><span className="text-center">Value</span><span className="text-center">Maximum</span><span>Conclusion</span>
              </div>
              <div className="space-y-0">
                {resolved.map((p) => {
                  const state = evalValue(readings[p.key], p.min, p.max);
                  const has = readings[p.key] !== undefined && readings[p.key] !== "";
                  return (
                    <div key={p.key} className="grid grid-cols-[1.4fr_0.7fr_0.9fr_0.7fr_1.6fr] gap-x-2 items-center" data-testid={`wt-row-${p.key}`}>
                      <span className="text-[13px] text-[#1a2b3c] whitespace-nowrap">{p.label}{p.unit && <span className="ml-1 text-[11px] font-bold text-slate-500" data-testid={`wt-unit-${p.key}`}>({p.unit})</span>}</span>
                      <span className="text-center text-[12px] text-slate-600">{p.min}</span>
                      <div className="flex justify-center">
                        <input value={readings[p.key] ?? ""} onChange={(e) => { dirty.current = true; setReadings((r) => ({ ...r, [p.key]: e.target.value })); }} onKeyDown={enterNext} data-wt-reading=""
                          readOnly={p.key === "combined_chlorine"} title={p.key === "combined_chlorine" ? "Locked — Total Chlorine minus Free Chlorine" : undefined}
                          className={winput + (p.key === "combined_chlorine" ? " !bg-slate-200 cursor-not-allowed" : "")} style={{ color: !has ? "#1a2b3c" : state === "inside" ? "#16a34a" : "#dc2626" }}
                          data-testid={`wt-val-${p.key}`} inputMode="decimal" />
                        <button onClick={() => setHistParam(p)} tabIndex={-1} title={`${p.label} history`} data-testid={`wt-hist-${p.key}`} className="ml-1 text-sky-600 hover:text-sky-800 hover:scale-110 transition-transform"><ChartSpline className="h-4 w-4" /></button>
                      </div>
                      <span className="text-center text-[12px] text-slate-600">{p.max}</span>
                      <div className="flex items-center gap-1.5">
                        {has && state === "inside" && (<><ThumbsUp className="h-4 w-4 text-[#2b3a8f]" fill="#2b3a8f" /><span className="text-[13px] text-[#2b3a8f]">Inside value</span></>)}
                        {has && state === "outside" && (<><ThumbsDown className="h-4 w-4 text-[#dc2626]" fill="#dc2626" /><span className="text-[13px] font-black text-[#dc2626]">Outside value</span></>)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <button onClick={() => { if (!Object.keys(readings).length) { toast.error("Enter readings first"); return; } setShowDosing(true); }} data-testid="wt-open-dosing"
                className={`mt-2 w-full py-2.5 rounded-md border-2 font-black text-[14px] flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 ${perfect ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-[#e08a1e] bg-[#fff1dc] text-[#b5610f] hover:bg-[#ffe6c2]"}`}>
                <FlaskConical className="h-5 w-5" />
                {perfect ? "Perfect water — Print Certificate / Send" : `Review Dosing & Send (${included.length} step${included.length === 1 ? "" : "s"}${mineralDose.applicable ? " + mineral options" : ""})`}
              </button>
              <div className="flex justify-end gap-2 mt-2">
                <span data-testid="wt-autosave-status" className={`px-3 py-1.5 text-[12px] font-black flex items-center gap-1.5 ${saveState === "error" ? "text-rose-600" : "text-emerald-700"}`}>
                  {saveState === "saving" ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : saveState === "saved" ? <><CheckCircle2 className="h-4 w-4" /> Saved automatically</> : saveState === "error" ? "Not saved — will retry" : "Auto-saves as you type"}
                </span>
              </div>
            </div>

            {/* body-of-water overview (read-only) — edit on Client Details */}
            <div className="w-full lg:w-96 shrink-0 bg-white/50 border border-[#7ba7d9] rounded-md p-2 flex flex-col" data-testid="wt-overview">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[13px] font-black text-[#123a63]">Body of Water Overview</span>
                <button onClick={() => onGoToClientDetails && onGoToClientDetails()} data-testid="wt-edit-water-details"
                  className="px-2.5 py-0.5 rounded-sm bg-[#123a63] text-white text-[11px] font-black hover:bg-[#1a4f8a]">Edit Water Details</button>
              </div>
              <div className="text-[12px] text-[#123a63]">
                {[
                  ["Body of Water", catLabel(activeBody.category)],
                  ["Type", activeBody.type],
                  ["Surface", activeBody.surface],
                  ["Volume", activeBody.volume_litres ? Number(activeBody.volume_litres).toLocaleString() + " L" : ""],
                  ["Filter", activeBody.filter_type],
                  ["Water Source", activeBody.water_source],
                  ["Salt Level", activeBody.salt_level],
                  ["Magnesium / TDS", activeBody.magnesium_level],
                  ["Chemical", activeBody.chemical_brand],
                  ["Spin Disk (auto)", `Disk ${disk}`],
                  ["Acid (pH)", activeBody.powder_acid ? "Powder — Lo n Slo" : activeBody.liquid_acid ? "Liquid — Pool Acid" : ""],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 border-b border-[#d7e3f0] py-0.5">
                    <span className="font-bold">{k}</span><span className="text-right text-[#1a2b3c]">{v || "—"}</span>
                  </div>
                ))}
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 pt-1.5 text-[11px] font-bold">
                  {activeBody.leafy && <span className="text-emerald-700 font-black">Leafy Area</span>}
                  {activeBody.covered && <span>Has Cover</span>}
                  {activeBody.inground && <span>Inground</span>}
                  {activeBody.above_ground && <span>Above Ground</span>}
                  {activeBody.residential && <span>Residential</span>}
                  {activeBody.commercial && <span>Commercial</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2 flex-1">
                {[["equipment_image_url", "Equipment"], ["pool_image_url", "Pool/Spa"]].map(([field, label]) => (
                  <div key={field} className="flex flex-col">
                    <div className="text-[11px] font-black text-[#123a63] text-center mb-0.5">{label}</div>
                    <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) uploadImg(field, f); }}
                      data-testid={`wt-img-${field}`} className="flex-1 min-h-[16rem] border-2 border-[#7ba7d9] rounded-lg bg-white/70 overflow-hidden flex items-center justify-center cursor-pointer hover:border-[#123a63] transition-colors">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadImg(field, f); }} />
                      {activeBody[field]
                        ? <img src={`${base}${activeBody[field]}`} alt={label} className="w-full h-full object-cover" />
                        : <span className="text-slate-400 text-[12px] font-bold text-center leading-6">NO IMAGE<br />DRAG &amp; DROP<br /><span className="text-[10px] font-normal">or click to choose</span></span>}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* LSI gauge removed per owner request to keep the panel on one screen */}

        </div>
      )}
      {showDosing && (
        <DosingPopup steps={steps} onSteps={editSteps} alternativesFor={alternativesFor} onSwap={swapProduct} perfect={perfect} onClose={() => setShowDosing(false)}
          onView={() => doReport(false)} onPrint={() => doReport(true)} onCertificate={printCert} onEmail={doEmail} onSms={doSms} onSell={onSellAtPos ? sellAtPos : null}
          onPrintSell={onSellAtPos ? () => { doReport(true); setShowDosing(false); sellAtPos(); } : null}
          mineral={mineralDose.applicable ? (
                <div className="mt-2 rounded-md border-2 border-[#2b8a9e] bg-[#eef9fb] p-2" data-testid="wt-mineral-dose">
                  <div className="flex items-center gap-1.5 text-[12px] font-black text-[#0e5a73] mb-1">
                    <FlaskConical className="h-4 w-4" /> Mineral Pool — Salt &amp; Mineral Top-Up
                  </div>
                  <div className="text-[11px] text-slate-600 mb-1.5">
                    Salt <b>{mineralDose.saltReading}</b> → aim {mineralDose.saltBand ? `${mineralDose.saltBand[0]}–${mineralDose.saltBand[1]}` : "—"} ·
                    Minerals <b>{mineralDose.magReading}</b> → aim {mineralDose.magBand[0]}–{mineralDose.magBand[1]}
                    <span className="ml-1 text-slate-400">· click one option to add it to the report</span>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2">
                    {mineralDose.options.map((opt, i) => (
                      <button type="button" key={i} onClick={() => chooseMineral(opt)} className={`text-left rounded-md border-2 p-2 transition-colors ${mineralStep?.mineral_label === opt.label ? "border-emerald-600 bg-emerald-50" : "border-[#9fcbd6] bg-white hover:border-[#2b8a9e]"}`} data-testid={`wt-mineral-opt-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[12px] font-black text-[#0e5a73]">{["①", "②", "③"][i]} {opt.label}{mineralStep?.mineral_label === opt.label ? " ✓ Selected" : ""}</span>
                          <span className="text-[13px] font-black text-emerald-700" data-testid={`wt-mineral-opt-total-${i}`}>${opt.total.toFixed(2)}</span>
                        </div>
                        {opt.lines.map((l, j) => (
                          <div key={j} className="flex justify-between text-[12px] text-[#123a63]">
                            <span>{l.qty} × {l.name}</span>
                            <span className="text-slate-500">${l.total.toFixed(2)}</span>
                          </div>
                        ))}
                      </button>
                    ))}
                  </div>
                </div>
          ) : null} />
      )}
      {rangeEdit && (
        <RangeOverrideModal
          category={cfg.category} type={cfg.type} overrides={cfg.range_overrides || {}} templatesByType={templatesByType}
          onSave={(ov) => { saveConfig({ range_overrides: ov }); setRangeEdit(false); }}
          onClose={() => setRangeEdit(false)}
        />
      )}
    </div>
  );
}

function RangeOverrideModal({ category, type, overrides, templatesByType, onSave, onClose }) {
  const rows = resolveAll({ type, category, range_overrides: overrides }, templatesByType);
  const [state, setState] = useState(() => { const s = {}; rows.forEach((p) => { s[p.key] = { enabled: p.enabled, min: p.min, max: p.max }; }); return s; });
  const upd = (k, field, v) => setState((s) => ({ ...s, [k]: { ...s[k], [field]: v } }));
  const save = () => {
    const ov = {};
    // a cleared Min/Max box keeps the inherited value instead of saving a blank
    rows.forEach((p) => { const mn = parseFloat(state[p.key].min), mx = parseFloat(state[p.key].max); ov[p.key] = { enabled: !!state[p.key].enabled, min: Number.isFinite(mn) ? mn : p.min, max: Number.isFinite(mx) ? mx : p.max }; });
    onSave(ov);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="wt-range-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-3 border-b"><span className="font-black text-slate-800">Adjust ideal ranges — this {catLabel(category).toLowerCase()}</span><button onClick={onClose}>✕</button></div>
        <p className="px-4 pt-2 text-[12px] text-slate-500">Tick which tests apply to this body of water and fine-tune each Min/Max. This overrides the type defaults for this pool only.</p>
        <div className="p-4 space-y-1">
          <div className="grid grid-cols-[1.6fr_0.6fr_0.9fr_0.9fr] gap-2 text-[11px] font-black text-slate-500"><span>Test</span><span className="text-center">Apply</span><span className="text-center">Min</span><span className="text-center">Max</span></div>
          {rows.map((p) => (
            <div key={p.key} className="grid grid-cols-[1.6fr_0.6fr_0.9fr_0.9fr] gap-2 items-center" data-testid={`wt-range-row-${p.key}`}>
              <span className="text-[13px]">{p.label}</span>
              <input type="checkbox" checked={!!state[p.key].enabled} onChange={(e) => upd(p.key, "enabled", e.target.checked)} data-testid={`wt-range-enable-${p.key}`} className="mx-auto h-4 w-4" />
              <input value={state[p.key].min} onChange={(e) => upd(p.key, "min", e.target.value)} data-testid={`wt-range-min-${p.key}`} className="rounded border border-slate-300 px-1 py-0.5 text-[12px] text-center" inputMode="decimal" />
              <input value={state[p.key].max} onChange={(e) => upd(p.key, "max", e.target.value)} data-testid={`wt-range-max-${p.key}`} className="rounded border border-slate-300 px-1 py-0.5 text-[12px] text-center" inputMode="decimal" />
            </div>
          ))}
        </div>
        <div className="p-3 border-t flex justify-end"><button onClick={save} data-testid="wt-range-save" className="rounded-lg bg-emerald-600 text-white font-bold px-5 py-2">Save ranges</button></div>
      </div>
    </div>
  );
}
